/**
 * 渠道列表接口 - 通用 API，各页面均可调用
 * GET /api/channels 返回系统已对接的渠道列表
 */
import { apiGet } from '@/utils/request';
import { parseListResponse, parsePaginatedResponse } from '@/utils/apiResponse';
import type { ContentOption } from '@/types/conditionBuilder';

/** 渠道项（/api/channels 返回的 list 元素） */
export interface ChannelItem {
  /** 渠道 id */
  id: number;
  /** 渠道 key，用于其他接口关联 */
  key: string;
  /** 渠道名称 */
  name: string;
  /** 描述 */
  description: string;
  /** 授权类型 */
  auth_type: string | null;
  /** 状态 */
  status: number;
  /** 排序 */
  sort: number;
  /** 账号数量 */
  account_count: number;
}

/** 渠道列表查询参数 */
export interface ChannelListParams {
  /** 1=只要有账号的渠道，0=只要没账号的；不传返回全部 */
  has_account?: 0 | 1;
}

/** 获取渠道列表，决定系统共有哪些对接的渠道 */
export function queryChannelsList(params?: ChannelListParams): Promise<ChannelItem[]> {
  return apiGet('/channels', params as Record<string, unknown> | undefined).then((d) =>
    parseListResponse<ChannelItem>(d),
  );
}

/** 渠道账号项 */
export interface ChannelAccount {
  id: number;
  name: string;
  channel_id: number;
  status: number;
  auth_status: number;
  external_id: string;
  [property: string]: any;
}

/** 获取渠道下的账号列表；传 robotId 时只返回绑定在该机器人下的账号 */
export function queryChannelAccounts(
  channelId: number,
  robotId?: number,
): Promise<ChannelAccount[]> {
  return (async () => {
    const list: ChannelAccount[] = [];
    const pageSize = 100;
    let page = 1;
    let total = 0;
    do {
      const response = await apiGet(`/channels/${channelId}/accounts`, {
        page,
        per_page: pageSize,
        ...(robotId ? { robot_id: robotId } : {}),
      }).then((data) => parsePaginatedResponse<ChannelAccount>(data));
      list.push(...response.list);
      total = response.total;
      page += 1;
      if (!response.list.length) break;
    } while (list.length < total);
    return list;
  })();
}

/**
 * 回显时单个渠道最多翻的页数：目标 id 已删除、已解绑时找不到，不设上限会把每个渠道的账号全部翻完；
 * 超过上限仍未找到的账号由调用方按 id 兜底显示。
 */
const ECHO_MAX_PAGES_PER_CHANNEL = 10;

/**
 * 所有渠道账号的远程分页选项。
 *
 * 账号接口按渠道分页，因此同一页会并行请求各渠道的对应页，再合并为一批选项；
 * keyword 始终传给后端，不在前端对已加载页做过滤。ids 仅用于编辑回显，
 * 会按接口分页继续查找，直到找到目标账号、遍历完所有页或达到单渠道翻页上限。
 */
export async function fetchAccountRemoteOptions(params: {
  page: number;
  pageSize: number;
  keyword: string;
  ids?: (string | number)[];
  robotId?: number;
}): Promise<{ list: Record<string, unknown>[]; total: number }> {
  const channels = await queryChannelsList({ has_account: 1 });
  const keyword = params.keyword.trim();

  const queryPage = async (channel: ChannelItem, page: number) => {
    const response = await apiGet(`/channels/${channel.id}/accounts`, {
      page,
      per_page: params.pageSize,
      keyword: keyword || undefined,
      ...(params.robotId ? { robot_id: params.robotId } : {}),
    }).then((data) => parsePaginatedResponse<ChannelAccount>(data));
    return {
      ...response,
      list: response.list.map((account) => ({
        ...account,
        id: account.id,
        channel_id: channel.id,
        name: `${channel.name} / ${account.name}`,
      })),
    };
  };

  const targetIds = new Set((params.ids ?? []).map(String));
  if (targetIds.size > 0) {
    const found = new Map<string, Record<string, unknown>>();
    await Promise.all(
      channels.map(async (channel) => {
        let page = 1;
        let total = 0;
        do {
          const response = await queryPage(channel, page);
          total = response.total;
          for (const account of response.list) {
            const id = String(account.id);
            if (targetIds.has(id)) found.set(id, account);
          }
          page += 1;
        } while (
          found.size < targetIds.size &&
          (page - 1) * params.pageSize < total &&
          page <= ECHO_MAX_PAGES_PER_CHANNEL
        );
      }),
    );
    const list = [...found.values()];
    return { list, total: list.length };
  }

  const pages = await Promise.all(channels.map((channel) => queryPage(channel, params.page)));
  return {
    list: pages.flatMap((response) => response.list),
    total: pages.reduce((sum, response) => sum + response.total, 0),
  };
}

/**
 * 来源渠道远程选项：只选到渠道，值为渠道 key。
 * 渠道数量少，一次返回全部，关键词在前端按名称过滤；ids 用于编辑回显。
 */
export async function fetchSourceRemoteOptions(params: {
  page: number;
  pageSize: number;
  keyword: string;
  ids?: (string | number)[];
}): Promise<{ list: Record<string, unknown>[]; total: number }> {
  const channels = await queryChannelsList();
  const keyword = params.keyword.trim().toLowerCase();
  const ids = new Set((params.ids ?? []).map(String));
  const list = channels
    .filter((channel) => ids.size === 0 || ids.has(channel.key))
    .filter((channel) => !keyword || channel.name.toLowerCase().includes(keyword))
    .map((channel) => ({ id: channel.key, name: channel.name }));
  return { list, total: list.length };
}

/**
 * 渠道/账号选项：不做结果缓存，每次调用都重新拉取。
 *
 * 来源级联数据随登录账号、以及渠道管理里的账号增删改实时变化，不缓存结果。
 * 这里只保留同一账号下的进行中请求用于并发去重；请求结束即释放。
 */
let sourceOptionsInflight: Promise<ContentOption[]> | null = null;
let sourceOptionsScope = '';

const getRequestScope = () => localStorage.getItem('token') ?? '';

/** 渠道→账号级联选项的构建，全量与按机器人过滤共用 */
async function buildSourceOptions(robotId?: number): Promise<ContentOption[]> {
  const channels = await queryChannelsList();
  return Promise.all(
    channels.map(async (ch) => {
      const accounts = await queryChannelAccounts(ch.id, robotId);
      return {
        value: ch.id,
        label: ch.name,
        children: accounts.map((acc) => ({ value: acc.id, label: acc.name })),
      } as ContentOption;
    }),
  );
}

/**
 * 获取来源渠道级联选项（渠道→账号）。
 * 签名受条件构建器的 OptionsFetcherFn 契约约束，不要在此加参数——
 * 按机器人过滤请用 fetchRobotSourceOptions。
 */
export function fetchSourceOptions(): Promise<ContentOption[]> {
  const scope = getRequestScope();
  if (sourceOptionsInflight && sourceOptionsScope === scope) return sourceOptionsInflight;
  const task = buildSourceOptions();
  sourceOptionsScope = scope;
  const current = task.finally(() => {
    if (sourceOptionsInflight !== current) return;
    sourceOptionsInflight = null;
    sourceOptionsScope = '';
  });
  sourceOptionsInflight = current;
  return sourceOptionsInflight;
}

/**
 * 只取绑定在指定机器人下的渠道账号（AI 测试用）。
 * 结果口径与全量不同，不参与 sourceOptionsInflight 去重，避免两者互相串结果。
 */
export function fetchRobotSourceOptions(robotId: number): Promise<ContentOption[]> {
  return buildSourceOptions(robotId);
}
